/**
 * Engine-aware literal formatting — the ESCAPING FALLBACK for engines whose
 * execution path cannot take driver-typed parameters (kusto mgmt commands,
 * the e2e mock adapter). Everything else binds through real driver
 * parameters; never widen the fallback without recording why. (The rare
 * mssql SSTS transport route errors loudly instead of falling back — see
 * mssqlRoutingAdapter's parameterizedSstsBlockedError.)
 */
import type { ParamValueType } from '../types/dashboardTypes';

export type LiteralResult = { ok: true; literal: string } | { ok: false; error: string };

const fail = (error: string): LiteralResult => ({ ok: false, error });

const isoUtc = (value: string): string | null => {
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString();
};

const stringLiteral = (engine: string, raw: string): LiteralResult => {
  if (raw.includes('\0')) return fail('string value contains a NUL byte');
  if (engine === 'kusto') {
    // KQL strings are backslash-escaped.
    return { ok: true, literal: `'${raw.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'` };
  }
  if (engine === 'mysql') {
    // mysql honors backslash escapes by default — double them too.
    return { ok: true, literal: `'${raw.replace(/\\/g, '\\\\').replace(/'/g, "''")}'` };
  }
  return { ok: true, literal: `'${raw.replace(/'/g, "''")}'` };
};

const dateLiteral = (engine: string, raw: string): LiteralResult => {
  const iso = isoUtc(raw);
  if (!iso) return fail(`invalid date value "${raw}"`);
  const noZone = iso.slice(0, 23); // YYYY-MM-DDTHH:mm:ss.SSS
  switch (engine) {
    case 'mssql':
      return { ok: true, literal: `CONVERT(datetime2, '${noZone}', 126)` };
    case 'pgsql':
      return { ok: true, literal: `TIMESTAMPTZ '${iso}'` };
    case 'mysql':
      // mysql datetimes are zone-naive — values are UTC-anchored by contract.
      return { ok: true, literal: `'${noZone.replace('T', ' ')}'` };
    case 'oracle':
      return { ok: true, literal: `TO_TIMESTAMP('${noZone}', 'YYYY-MM-DD"T"HH24:MI:SS.FF3')` };
    case 'sqlite':
      return { ok: true, literal: `'${noZone}'` };
    case 'kusto':
      return { ok: true, literal: `datetime(${iso})` };
    default:
      return { ok: true, literal: `'${noZone}'` };
  }
};

const booleanLiteral = (engine: string, raw: string): LiteralResult => {
  const truthy = raw === 'true' || raw === '1';
  const falsy = raw === 'false' || raw === '0';
  if (!truthy && !falsy) return fail(`invalid boolean value "${raw}"`);
  switch (engine) {
    case 'pgsql':
    case 'mysql':
      return { ok: true, literal: truthy ? 'TRUE' : 'FALSE' };
    case 'kusto':
      return { ok: true, literal: truthy ? 'true' : 'false' };
    default:
      // mssql/sqlite/oracle have no SQL boolean literal — bind as bit/number.
      return { ok: true, literal: truthy ? '1' : '0' };
  }
};

/** Format one typed value as an engine-safe inline literal. */
export function formatLiteral(engine: string, type: ParamValueType, raw: string): LiteralResult {
  switch (type) {
    case 'number': {
      if (raw.trim() === '' || !Number.isFinite(Number(raw))) {
        return fail(`invalid number value "${raw}"`);
      }
      return { ok: true, literal: String(Number(raw)) };
    }
    case 'date':
      return dateLiteral(engine, raw);
    case 'boolean':
      return booleanLiteral(engine, raw);
    default:
      return stringLiteral(engine, raw);
  }
}
